import React, { useState } from "react";
import {
  TextField, Button, Box, IconButton, Grid,
  Checkbox, FormControlLabel, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LabelIcon from "@mui/icons-material/Label";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const MandateLeaveForm = ({ handleClose }) => {
  const [formValues, setFormValues] = useState({
    email: "",
    studentName: "",
    assignedNilai: "",
    teacherId: "",
    parentName: "",
    parentEmail: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    Object.keys(formValues).forEach((key) => {
      if (!formValues[key]) newErrors[key] = "Required";
    });
    if (!agreed) newErrors.agreed = "You must agree to the absence policy.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length === 0) {
      try {
        await postData("student/leave/create", { ...formValues, leaveType: "mandate" });
        setToast({ open: true, severity: "success", message: "Mandate leave submitted successfully" });
        setTimeout(() => handleClose(), 800);
      } catch (_) {
        setToast({ open: true, severity: "error", message: "Failed to submit mandate leave" });
      }
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      "& fieldset": { borderColor: "#D5D7DA" },
      "&:hover fieldset": { borderColor: "#B0B5C8" },
      "&.Mui-focused fieldset": { borderColor: "#244C77" },
    },
    "& .MuiInputBase-input": { fontSize: "13px", color: "#374151" },
    "& .MuiInputBase-input::placeholder": { color: "#9CA3AF", opacity: 1 },
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        width: 560,
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: "28px 32px 24px",
        position: "relative",
      }}
    >
      <Toast
        open={toast.open}
        handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity}
        message={toast.message}
      />

      {/* ── Header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Icon box */}
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              border: "1.5px solid #E5E7EB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FAFAFA",
            }}
          >
            <LabelIcon sx={{ color: "#6B7280", fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", color: "#111827", lineHeight: 1.3 }}>
              Mandate Leave Form
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#9CA3AF", mt: 0.3 }}>
              Labels help organize projects.
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#6B7280", mt: 0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Fields ── */}
      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="email" type="email"
            placeholder="Email"
            value={formValues.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="studentName"
            placeholder="Student's Name"
            value={formValues.studentName}
            onChange={handleChange}
            error={!!errors.studentName}
            helperText={errors.studentName}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 2 */}
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="assignedNilai"
            placeholder="Assigned Nilai"
            value={formValues.assignedNilai}
            onChange={handleChange}
            error={!!errors.assignedNilai}
            helperText={errors.assignedNilai}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="teacherId"
            placeholder="Teacher's ID"
            value={formValues.teacherId}
            onChange={handleChange}
            error={!!errors.teacherId}
            helperText={errors.teacherId}
            sx={fieldSx}
          />
        </Grid>

        {/* Row 3 */}
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="parentName"
            placeholder="Parent's Name"
            value={formValues.parentName}
            onChange={handleChange}
            error={!!errors.parentName}
            helperText={errors.parentName}
            sx={fieldSx}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth size="small"
            name="parentEmail" type="email"
            placeholder="Parent's Email"
            value={formValues.parentEmail}
            onChange={handleChange}
            error={!!errors.parentEmail}
            helperText={errors.parentEmail}
            sx={fieldSx}
          />
        </Grid>

        {/* Declaration */}
        <Grid item xs={12} sx={{ mt: 0.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  if (errors.agreed) setErrors((prev) => ({ ...prev, agreed: "" }));
                }}
                sx={{
                  color: "#D5D7DA",
                  "&.Mui-checked": { color: "#2563EB" },
                  padding: "4px 8px 4px 4px",
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#2563EB",
                  letterSpacing: "0.01em",
                }}
              >
                Declaration of Agreement to Abide by the Absence Policy.
              </Typography>
            }
            sx={{ alignItems: "center", ml: 0 }}
          />
          {errors.agreed && (
            <Typography sx={{ color: "#f44336", fontSize: "11px", ml: "30px", mt: "-4px" }}>
              {errors.agreed}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* ── Buttons ── */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button
          onClick={() => { handleClose(); setErrors({}); }}
          sx={{
            minWidth: 130,
            border: "1.5px solid #D5D7DA",
            borderRadius: "10px",
            color: "#6B7280",
            fontSize: "14px",
            fontWeight: 500,
            textTransform: "none",
            px: 4,
            py: 1,
            "&:hover": { backgroundColor: "#F9FAFB", borderColor: "#B0B5C8" },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          sx={{
            minWidth: 130,
            borderRadius: "10px",
            backgroundColor: "#2D3A5C",
            color: "#fff",
            fontSize: "14px",
            fontWeight: 500,
            textTransform: "none",
            px: 4,
            py: 1,
            "&:hover": { backgroundColor: "#1E2A45" },
          }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};

export default MandateLeaveForm;
